import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Calendar, ChevronDown } from 'lucide-react';

/**
 * GA Event Tracking helper
 */
const trackEvent = (eventName, params) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
  } else {
    console.log('GA Event:', eventName, params);
  }
};

/**
 * Format date for calendar URLs
 * Google/Yahoo use: YYYYMMDDTHHMMSSZ
 * ICS uses: YYYYMMDDTHHMMSS with TZID
 */
const formatDateForCalendar = (dateStr, includeTime = false) => {
  const date = new Date(dateStr);
  if (includeTime) {
    return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  }
  // All-day event format
  return dateStr.replace(/-/g, '');
};

/**
 * Generate Google Calendar URL
 */
const getGoogleCalendarUrl = (event) => {
  const startDate = formatDateForCalendar(event.dates.start);
  const endDate = formatDateForCalendar(event.dates.end);
  
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.name,
    dates: `${startDate}/${endDate}`,
    details: `${event.shortDescription}\n\nMore info: ${event.officialUrl || window.location.href}`,
    location: `${event.venueName}, ${event.city}`,
    sf: 'true',
  });
  
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

/**
 * Generate Outlook Web URL
 */
const getOutlookWebUrl = (event) => {
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: event.name,
    startdt: event.dates.start,
    enddt: event.dates.end,
    body: `${event.shortDescription}\n\nMore info: ${event.officialUrl || window.location.href}`,
    location: `${event.venueName}, ${event.city}`,
    allday: 'true',
  });
  
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
};

/**
 * Generate Yahoo Calendar URL
 */
const getYahooCalendarUrl = (event) => {
  const startDate = formatDateForCalendar(event.dates.start);
  const endDate = formatDateForCalendar(event.dates.end);
  
  const params = new URLSearchParams({
    v: '60',
    title: event.name,
    st: startDate,
    et: endDate,
    desc: `${event.shortDescription}\n\nMore info: ${event.officialUrl || window.location.href}`,
    in_loc: `${event.venueName}, ${event.city}`,
    dur: 'allday',
  });
  
  return `https://calendar.yahoo.com/?${params.toString()}`;
};

/**
 * Generate ICS file content for Apple Calendar / Outlook Desktop
 */
const generateICSContent = (event) => {
  const uid = `${event.slug}@jigsawconferences.co.uk`;
  const now = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const startDate = event.dates.start.replace(/-/g, '');
  const endDate = event.dates.end.replace(/-/g, '');
  
  // Escape special characters for ICS
  const escapeICS = (str) => {
    if (!str) return '';
    return str
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\n/g, '\\n');
  };
  
  const description = escapeICS(`${event.shortDescription}\\n\\nMore info: ${event.officialUrl || window.location.href}`);
  const location = escapeICS(`${event.venueName}, ${event.city}`);
  const summary = escapeICS(event.name);
  
  return `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Jigsaw Conferences//Events Hub//EN
CALSCALE:GREGORIAN
METHOD:PUBLISH
X-WR-CALNAME:${summary}
X-WR-TIMEZONE:Europe/London
BEGIN:VTIMEZONE
TZID:Europe/London
BEGIN:DAYLIGHT
TZOFFSETFROM:+0000
TZOFFSETTO:+0100
TZNAME:BST
DTSTART:19700329T010000
RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=-1SU
END:DAYLIGHT
BEGIN:STANDARD
TZOFFSETFROM:+0100
TZOFFSETTO:+0000
TZNAME:GMT
DTSTART:19701025T020000
RRULE:FREQ=YEARLY;BYMONTH=10;BYDAY=-1SU
END:STANDARD
END:VTIMEZONE
BEGIN:VEVENT
UID:${uid}
DTSTAMP:${now}
DTSTART;VALUE=DATE:${startDate}
DTEND;VALUE=DATE:${endDate}
SUMMARY:${summary}
DESCRIPTION:${description}
LOCATION:${location}
URL:${event.officialUrl || window.location.href}
STATUS:CONFIRMED
TRANSP:TRANSPARENT
END:VEVENT
END:VCALENDAR`;
};

/**
 * Download ICS file
 */
const downloadICS = (event) => {
  const icsContent = generateICSContent(event);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = `${event.slug}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * AddToCalendarButton Component
 * 
 * Props:
 * - event: Event object with name, dates, venueName, city, shortDescription, officialUrl, slug
 * - variant: 'default' | 'outline' | 'ghost' (button style)
 * - size: 'default' | 'sm' | 'lg'
 * - className: Additional CSS classes
 */
export default function AddToCalendarButton({ 
  event, 
  variant = 'outline',
  size = 'default',
  className = ''
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (!event || !event.dates?.start) {
    return null;
  }

  const handleCalendarClick = (method, url) => {
    // Track GA event
    trackEvent('events_hub_add_to_calendar_click', {
      event_name: event.name,
      city: event.citySlug || event.city,
      slug: event.slug,
      method: method
    });

    if (method === 'apple' || method === 'outlook-desktop') {
      downloadICS(event);
    } else if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    
    setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={variant} 
          size={size}
          className={`${className}`}
          data-testid="add-to-calendar-button"
        >
          <Calendar className="w-4 h-4 mr-2" />
          {t('common.addToCalendar')}
          <ChevronDown className="w-4 h-4 ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem 
          onClick={() => handleCalendarClick('google', getGoogleCalendarUrl(event))}
          className="cursor-pointer"
          data-testid="calendar-google"
        >
          <svg className="w-4 h-4 mr-3" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 100-16 8 8 0 000 16zm1-8h4v2h-6V7h2v5z"/>
          </svg>
          Google Calendar
        </DropdownMenuItem>
        
        <DropdownMenuItem 
          onClick={() => handleCalendarClick('outlook-web', getOutlookWebUrl(event))}
          className="cursor-pointer"
          data-testid="calendar-outlook-web"
        >
          <svg className="w-4 h-4 mr-3" viewBox="0 0 24 24" fill="currentColor">
            <path d="M7.88 12.04q0 .45-.11.87-.1.41-.33.74-.22.33-.58.52-.37.2-.87.2t-.85-.2q-.35-.21-.57-.55-.22-.33-.33-.75-.1-.42-.1-.86t.1-.87q.1-.43.34-.76.22-.34.59-.54.36-.2.87-.2t.86.2q.35.21.57.55.22.34.31.77.1.43.1.88zM24 12v9.38q0 .46-.33.8-.33.32-.8.32H7.13q-.46 0-.8-.33-.32-.33-.32-.8V18H1q-.41 0-.7-.3-.3-.29-.3-.7V7q0-.41.3-.7Q.58 6 1 6h6.5V2.55q0-.44.3-.75.3-.3.75-.3h12.9q.44 0 .75.3.3.3.3.75V12zm-6-8.25v3h3v-3zm0 4.5v3h3v-3zm0 4.5v1.83l3.05-1.83zm-5.25-9v3h3.75v-3zm0 4.5v3h3.75v-3zm0 4.5v2.03l2.41 1.5 1.34-.8v-2.73zM9 3.75V6h2l.13.01.12.04v-2.3zM5.98 15.98q.9 0 1.6-.3.7-.32 1.19-.86.48-.55.73-1.28.25-.74.25-1.61 0-.83-.25-1.55-.24-.71-.71-1.24t-1.15-.83q-.68-.3-1.55-.3-.92 0-1.64.3-.71.3-1.2.85-.5.54-.75 1.3-.25.74-.25 1.63 0 .85.26 1.56.26.72.74 1.23.48.52 1.17.81.69.3 1.56.3zM7.5 21h12.39L12 16.08V17q0 .41-.3.7-.29.3-.7.3H7.5zm15-.13v-7.24l-5.9 3.54Z"/>
          </svg>
          Outlook (Web)
        </DropdownMenuItem>
        
        <DropdownMenuItem 
          onClick={() => handleCalendarClick('outlook-desktop')}
          className="cursor-pointer"
          data-testid="calendar-outlook-desktop"
        >
          <svg className="w-4 h-4 mr-3" viewBox="0 0 24 24" fill="currentColor">
            <path d="M7.88 12.04q0 .45-.11.87-.1.41-.33.74-.22.33-.58.52-.37.2-.87.2t-.85-.2q-.35-.21-.57-.55-.22-.33-.33-.75-.1-.42-.1-.86t.1-.87q.1-.43.34-.76.22-.34.59-.54.36-.2.87-.2t.86.2q.35.21.57.55.22.34.31.77.1.43.1.88zM24 12v9.38q0 .46-.33.8-.33.32-.8.32H7.13q-.46 0-.8-.33-.32-.33-.32-.8V18H1q-.41 0-.7-.3-.3-.29-.3-.7V7q0-.41.3-.7Q.58 6 1 6h6.5V2.55q0-.44.3-.75.3-.3.75-.3h12.9q.44 0 .75.3.3.3.3.75V12zm-6-8.25v3h3v-3zm0 4.5v3h3v-3zm0 4.5v1.83l3.05-1.83zm-5.25-9v3h3.75v-3zm0 4.5v3h3.75v-3zm0 4.5v2.03l2.41 1.5 1.34-.8v-2.73zM9 3.75V6h2l.13.01.12.04v-2.3zM5.98 15.98q.9 0 1.6-.3.7-.32 1.19-.86.48-.55.73-1.28.25-.74.25-1.61 0-.83-.25-1.55-.24-.71-.71-1.24t-1.15-.83q-.68-.3-1.55-.3-.92 0-1.64.3-.71.3-1.2.85-.5.54-.75 1.3-.25.74-.25 1.63 0 .85.26 1.56.26.72.74 1.23.48.52 1.17.81.69.3 1.56.3zM7.5 21h12.39L12 16.08V17q0 .41-.3.7-.29.3-.7.3H7.5zm15-.13v-7.24l-5.9 3.54Z"/>
          </svg>
          Outlook (Desktop)
        </DropdownMenuItem>
        
        <DropdownMenuItem 
          onClick={() => handleCalendarClick('apple')}
          className="cursor-pointer"
          data-testid="calendar-apple"
        >
          <svg className="w-4 h-4 mr-3" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
          </svg>
          Apple Calendar
        </DropdownMenuItem>
        
        <DropdownMenuItem 
          onClick={() => handleCalendarClick('yahoo', getYahooCalendarUrl(event))}
          className="cursor-pointer"
          data-testid="calendar-yahoo"
        >
          <svg className="w-4 h-4 mr-3" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12.006 2C6.494 2 2.003 6.491 2.003 12.003c0 5.512 4.49 10.003 10.003 10.003 5.512 0 10.003-4.491 10.003-10.003C22.009 6.491 17.518 2 12.006 2zm3.615 5.933l-2.253 5.082h-.027l-2.228-5.082H9.057l3.297 7.161-.037.076c-.403.85-.788 1.211-1.475 1.211-.338 0-.616-.037-.824-.1l-.25 1.612c.35.137.8.212 1.35.212 1.462 0 2.338-.75 3.038-2.438l3.638-7.734h-2.173z"/>
          </svg>
          Yahoo Calendar
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
